import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { completeFirstLoginPasswordChange } from '../../services/careAuthService';
import './ChangePassword.css';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Self-service password change: re-authenticates with the current password,
 * updates the Firebase Auth password, then asks the backend to clear
 * users/{uid}.mustChangePassword and mark the email verified (the client cannot
 * write those fields — see firestore.rules).
 * Used by both the forced first-login screen and the header "Change password" modal.
 */
export default function ChangePasswordForm({ onSuccess, currentPasswordLabel = 'Current password', submitLabel = 'Change password' }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current password.');
      return;
    }
    const user = auth.currentUser;
    if (!user?.email) {
      setError('Your session has expired. Sign out and sign in again.');
      return;
    }
    setSubmitting(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      try {
        await completeFirstLoginPasswordChange();
        // Pick up emailVerified (required before MFA enrollment) on the client user object.
        await user.reload();
        await user.getIdToken(true);
      } catch (err) {
        // Non-fatal: the Auth password is already changed.
        console.warn('Failed to finalize password change:', err?.message || err);
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (onSuccess) await onSuccess();
    } catch (err) {
      if (err?.code === 'auth/invalid-credential' || err?.code === 'auth/wrong-password') {
        setError('The current password is incorrect.');
      } else if (err?.code === 'auth/weak-password') {
        setError('The new password is too weak. Use a longer, less common password.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many attempts. Wait a few minutes and try again.');
      } else {
        setError(err?.message || 'Password change failed. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="cp-form">
      {error && <div className="cp-error" role="alert">{error}</div>}
      <label className="cp-label">
        {currentPasswordLabel}
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
          className="cp-input"
        />
      </label>
      <label className="cp-label">
        New password
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          className="cp-input"
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
        />
      </label>
      <label className="cp-label">
        Confirm new password
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          className="cp-input"
        />
      </label>
      <button type="submit" className="cp-submit" disabled={submitting}>
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </form>
  );
}
